import { DateTime } from "luxon";
import {
	type SchedulePushConfig,
	SchedulePushLocationStatus,
	SchedulePushLoginStatus,
	SchedulePushMappingMode,
	SchedulePushSource,
	SchedulePushWorkgroupRule,
} from "./schedulePushTypes";

/**
 * Whether a config GET answered with the shape the panel reads (`schedulePushTypes.ts`). The section and its header
 * chip read the answer straight away, so an empty body, an HTML error page or a field of the wrong kind must become a
 * load error the section shows — not a crash on `config.target.active`. Dates are checked as Luxon values because
 * `axiosInstance` converts every ISO string on the way in.
 */
export const isSchedulePushConfig = (value: unknown): value is SchedulePushConfig =>
	isRecord(value) &&
	isOneOf(SchedulePushSource, value.source) &&
	typeof value.vendorName === "string" &&
	isOneOf(SchedulePushMappingMode, value.mappingMode) &&
	(value.target === null || isTarget(value.target)) &&
	isStringOrNull(value.integrationFacilityCode) &&
	typeof value.integrationActive === "boolean" &&
	typeof value.facilityCodeEditable === "boolean" &&
	typeof value.hasPushRecords === "boolean" &&
	isOneOf(SchedulePushLoginStatus, value.loginStatus) &&
	isListOf(value.mapping, isMapRow) &&
	isWindow(value.window) &&
	isListOf(value.locations, isLocationRow) &&
	(value.noLocation === null || isNoLocationRow(value.noLocation)) &&
	(value.workgroups === null || isWorkgroups(value.workgroups));

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const isStringOrNull = (value: unknown): value is string | null => value === null || typeof value === "string";

const isListOf = (value: unknown, isItem: (item: unknown) => boolean): boolean => Array.isArray(value) && value.every(isItem);

const isOneOf = (values: Record<string, string>, value: unknown): boolean => typeof value === "string" && Object.values(values).includes(value);

const isTarget = (value: unknown): boolean =>
	isRecord(value) &&
	typeof value.facilityCode === "string" &&
	typeof value.active === "boolean" &&
	typeof value.updatedBy === "string" &&
	DateTime.isDateTime(value.updatedAt);

const isWindow = (value: unknown): boolean => isRecord(value) && DateTime.isDateTime(value.start) && DateTime.isDateTime(value.end);

const isMapRow = (value: unknown): boolean => isRecord(value) && isStringOrNull(value.location) && typeof value.unitName === "string";

const isLocationRow = (value: unknown): boolean =>
	isRecord(value) &&
	typeof value.location === "string" &&
	typeof value.shiftCount === "number" &&
	isOneOf(SchedulePushLocationStatus, value.status) &&
	typeof value.configured === "boolean" &&
	isStringOrNull(value.unitName) &&
	isListOf(value.spellingOf, (item) => typeof item === "string") &&
	isStringOrNull(value.suggestedUnitName);

const isNoLocationRow = (value: unknown): boolean =>
	isRecord(value) && typeof value.shiftCount === "number" && isOneOf(SchedulePushLocationStatus, value.status) && isStringOrNull(value.unitName);

const isWorkgroups = (value: unknown): boolean =>
	isRecord(value) &&
	typeof value.tenant === "string" &&
	typeof value.location === "string" &&
	isListOf(value.businessUnits, (item) => typeof item === "string") &&
	isOneOf(SchedulePushWorkgroupRule, value.rule);
